<script>
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import AssignBranchesModal from './AssignBranchesModal.vue';

export default {
  name: 'ServiceDetails',
  components: {
    PageHeader,
    LoadingState,
    ErrorState,
    EmptyState,
    AssignBranchesModal,
  },
  data() {
    return {
      serviceData: [],
      isLoading: false,
      isError: false,
      showAssignModal: false,
      promotions: [],
    }
  },
  computed: {
    serviceId() {
      return this.$route.params.serviceId;
    },
    serviceName() {
      return this.serviceData.service?.name || '';
    },
    branches() {
      return this.serviceData.branches || [];
    }
  },
  methods: {
    async fetchServiceDetails() {
      try {
        this.isLoading = true;
        this.isError = false;
        const res = await this.$store.dispatch('serviceV2/fetchServiceById', { serviceId: this.serviceId });
        this.serviceData = res;
        console.log('[DEBUG ServiceDetails] serviceId:', this.serviceId, 'type:', typeof this.serviceId);
        this.promotions = await this.$store.dispatch(
          'promotions/getPromotionsWithBranchesForService',
          {
            accountId: this.$store.getters.getCurrentAccountId,
            serviceId: this.serviceId
          }
        );
      } catch(err) {
        console.error(err);
        this.isError = true;
        useAlert(err);
      } finally {
        this.isLoading = false;
      }
    },
    openAsignModal() {
      this.showAssignModal = true;
    },
  },
  async mounted() {
    await this.fetchServiceDetails();
  }
}
</script>

<template>
  <div class="p-4 md:p-8 space-y-6 overflow-y-auto">
    <!-- Modals -->
    <AssignBranchesModal 
      v-if="showAssignModal" 
      :branches="branches"
      @close="showAssignModal = false" 
      @refresh="fetchServiceDetails" 
    />

    <!-- States -->
    <LoadingState v-if="isLoading" message="Loading service..." />
    <ErrorState v-else-if="!isLoading && isError" />
    <EmptyState
      v-else-if="!isLoading && !isError && !serviceData.service"
      title="No Service Data Available"
      message="Data not found"
    />

    <!-- Page Header -->
    <PageHeader
      v-else
      :title="`Services Management / ${serviceName}`"
      icon="service"
      :allow-back="true"
      description="Create and manage services with ease. Define service details, link e-vouchers for special offers, and organize them for better accessibility. These services can also be assigned to specific branches to keep your service catalog consistent across locations."
    />

    <!-- Main Content Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- LEFT SIDE — 2 COLUMNS STACKED -->
      <div class="space-y-6 col-span-2">

        <!-- Service Info -->
        <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <h2 class="text-lg md:text-xl font-semibold">Service Details</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2">
              <p><span class="font-medium">Name:</span> {{ serviceData.service.name }}</p>
              <p><span class="font-medium">Description:</span> {{ serviceData.service.description || '-' }}</p>
              <p><span class="font-medium">Duration:</span> {{ serviceData.service.duration }} hour(s)</p>
              <p><span class="font-medium">Price:</span> {{ serviceData.service.price || '-' }}</p>
              <p><span class="font-medium">Max Serve:</span> {{ serviceData.service.max_serve }}</p>
              <p><span class="font-medium">Active:</span> {{ serviceData.service.is_active ? 'Yes' : 'No' }}</p>
              <p v-if="serviceData.service.disabled_reason">
                <span class="font-medium">Disabled Reason:</span> {{ serviceData.service.disabled_reason }}
              </p>
            </div>

            <div class="flex justify-center items-center">
              <img
                v-if="serviceData.service.images[0]"
                :src="serviceData.service.images[0].presigned_url"
                alt="Service Image"
                class="rounded-lg shadow max-h-48 object-cover w-full"
              />
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <h2 class="text-lg md:text-xl font-semibold">Stats</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Total Branches: {{ serviceData.stats.total_branches }}</div>
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Enabled Branches: {{ serviceData.stats.enabled_branches }}</div>
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Total Staff: {{ serviceData.stats.total_staff }}</div>
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Active Staff: {{ serviceData.stats.active_staff }}</div>
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Total Services: {{ serviceData.stats.total_services }}</div>
            <div class="p-3 bg-slate-50 dark:bg-slate-700 rounded">Enabled Services: {{ serviceData.stats.enabled_services }}</div>
          </div>
        </div>

        <!-- Service Images -->
        <div v-if="serviceData.service.images.length"
             class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <h2 class="text-lg md:text-xl font-semibold">Service Images</h2>
          <div class="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            <div v-for="img in serviceData.service.images" :key="img.id" class="text-center">
              <img
                :src="img.presigned_url"
                alt="Service Image"
                class="rounded-lg shadow object-cover w-full h-32"
              />
              <p class="text-xs text-gray-500 mt-1">{{ new Date(img.created_at).toLocaleDateString() }}</p>
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT SIDE — 1 COLUMN FOR BRANCHES -->
      <div class="space-y-6">

        <!-- Branches -->
        <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <div class="flex justify-between gap-4">
            <h2 class="text-lg md:text-xl font-semibold">Branches</h2>
            <woot-button icon="add" @click="openAsignModal">Assign Branches</woot-button>
          </div>

          <p v-if="!serviceData.branches.length" class="text-gray-500">
            No branch assigned.
          </p>

          <ul v-else class="space-y-2 max-h-80 overflow-y-auto">
            <li
              v-for="branch in serviceData.branches"
              :key="branch.id"
              class="flex justify-between items-center p-2 border-b border-gray-200 dark:border-slate-600"
            >
              <span>{{ branch.name }}</span>
              <span
                :class="[
                  'text-sm px-2 py-1 rounded',
                  branch.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                ]"
              >
                {{ branch.is_active ? 'Active' : 'Inactive' }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Promotions -->
        <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
          <h2 class="text-lg md:text-xl font-semibold">Promotions</h2>

          <p v-if="!promotions.length" class="text-gray-500">
            No promotions linked to this service.
          </p>

          <ul v-else class="space-y-3 max-h-80 overflow-y-auto">
            <li
              v-for="promo in promotions"
              :key="promo.id"
              class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 space-y-2"
            >
              <!-- Header -->
              <div class="flex justify-between items-center">
                <p class="font-medium">{{ promo.promotion_name }}</p>
                <span
                  :class="[
                    'text-xs px-2 py-1 rounded',
                    promo.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                      : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                  ]"
                >
                  {{ promo.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>

              <!-- Description -->
              <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ promo.description || '-' }}
              </p>

              <!-- Price & Expiry -->
              <div class="text-sm flex flex-wrap gap-4">
                <span><strong>Price:</strong> {{ promo.price }}</span>
                <span v-if="promo.expired_date">
                  <strong>Expires:</strong> {{ promo.expired_date }}
                </span>
              </div>

              <!-- Branches using this promotion -->
              <div class="text-sm">
                <p class="font-medium mb-1">Used by branches:</p>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="branch in promo.branches"
                    :key="branch.id"
                    class="text-xs px-2 py-1 rounded bg-slate-200 dark:bg-slate-600"
                  >
                    {{ branch.name }}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

    </div>
  </div>
</template>